/* eslint-disable react/jsx-curly-brace-presence */

import React from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';

import App from 'containers/App/App';

import { isLoaded as isAuthLoaded, load as loadAuth } from 'redux/modules/auth';
import { get, toSingular } from 'helpers/HelperFunctions';
import {
  getBuyerLoginPath,
  getSellerLoginPath,
} from 'helpers/clientOnboarding';

import {
  AnswerPublicQuestion,
  ApplyForVerification,
  Ari,
  AriJobLanding,
  Aup,
  BetterPic,
  Copyright,
  EngagementTerms,
  HowItWorks,
  MicroProjects,
  NotFound,
  Notifications,
  Onboarding,
  OnboardingClient,
  OnboardingNotifications,
  PlatformTerms,
  Pricing,
  Post,
  PostAJobCategory,
  PostAJobIntent,
  PostAJobRole,
  PostAJobSkills,
  PostAJobCapacity,
  PostAJobType,
  PostAJobDuration,
  PostAJobExperience,
  PostAJobLikelihood,
  PostAJobLocations,
  PostAJobTitleAndDescription,
  PostAJobDeadline,
  PostAJobExamples,
  PostAJobBudget,
  PostAJobAboutYou,
  PostAJobEmail,
  PostAJobUnpaidWork,
  PostAJobFlowShell,
  ReportMessages,
  SignUp,
  SignIn,
  Shortlist,
  ViewEarnings,
  ViewPayments,
  Undock,
  JobDetails,
  RecentJobRedirect,
} from 'containers/Universals';
import Home from 'containers/Home/Home';
import Portfolio from 'containers/Portfolio/Portfolio';
import BrowseOld from 'containers/BrowseOld/BrowseOld';
import Browse from 'containers/Browse/Browse';
import Categories from 'containers/Categories/Categories';

import WhyUseTwine from 'containers/LandingPages/WhyUseTwine/WhyUseTwine';
import Enterprise from 'containers/LandingPages/Enterprise/Enterprise';
import Messages from 'containers/Messages/Messages';
import Project from 'containers/Project/Project';
import Settings from 'containers/Settings/Settings';
import TestimonialsUpdate from 'containers/Testimonials/TestimonialsUpdate';
import Projects from 'containers/Projects/Projects';
import CreateProject from 'containers/TextProjects/CreateProject';
import EditProject from 'containers/TextProjects/EditProject';
import ManageProjects from 'containers/ManageProjects/ManageProjects';
import {
  BriefDetailsWrapper,
  ManageBriefsWrapper,
  ManagePitchesWrapper,
  FindRedirectWrapper,
} from './redirectWrappers';
import { getRedirRoute } from 'helpers/Redirections';
import Activate from 'containers/Activate/Activate';
import { AuthCode } from 'containers/Universals';
import Redirecting from 'containers/Redirecting/Redirecting';
import SavedJobs from 'containers/SavedJobs/SavedJobs';
import Dashboard from 'containers/ClientATS/Dashboard';
import { STEP_PATHS } from 'containers/PostAJob/flow/steps';
import Loader from 'components/Loader/Loader';

export default ({ dispatch, getState }) => {
  const checkAuth = () => get(getState(), ['auth', 'user'], null);

  let user = checkAuth();

  if (!isAuthLoaded(getState())) {
    dispatch(loadAuth())
      .then(() => {
        user = checkAuth();
      })
      .catch(() => {});
  } else {
    user = checkAuth();
  }

  const isLoggedIn = () => !!getState().auth.user;

  // Shared function to handle authentication redirects
  // Returns true if a redirect was performed, false otherwise
  const handleAuthRedirect = (user) => {
    if (user && __CLIENT__) {
      const urlParams = new URLSearchParams(window.location.search);
      const referer = urlParams.get('referer');

      // Check if we're trying to redirect to /signin or /signup when we're already there
      // This prevents an infinite redirect loop
      if (
        referer === 'signin' ||
        referer === '/signin' ||
        referer === 'signup' ||
        referer === '/signup' ||
        !referer
      ) {
        // Don't redirect back to signin
        const redirectPath =
          user?.type === 'buyer' ? getBuyerLoginPath() : getSellerLoginPath();
        if (window.location.pathname !== redirectPath) {
          window.location = redirectPath;
          return true;
        }
      } else if (referer) {
        // Format the referer path properly
        const redirectPath = referer.startsWith('/') ? referer : `/${referer}`;
        // Only redirect if we're not already on this page
        if (window.location.pathname !== redirectPath) {
          window.location = redirectPath;
          return true;
        }
      } else {
        // Default redirect based on user type
        const redirectPath =
          user?.type === 'buyer' ? getBuyerLoginPath() : getSellerLoginPath();
        if (window.location.pathname !== redirectPath) {
          window.location = redirectPath;
          return true;
        }
      }
    }
    return false;
  };

  const createAuthGuard = (Component) => (props) => {
    const user = useSelector((state) => state.auth.user);
    const isAuthLoaded = useSelector((state) => state.reduxAsyncConnect.loaded);

    if (!isAuthLoaded) {
      return <Loader loading />;
    }

    if (handleAuthRedirect(user)) {
      return null;
    }

    return <Component {...props} />;
  };

  const AuthenticatedAuthCodeGuard = createAuthGuard(AuthCode);

  const signInUrl = process.env.ENDPOINT_FRONTEND
    ? `https://${process.env.ENDPOINT_FRONTEND}`
    : 'http://local.twine.net';

  const requireLoginRender = (Component, url) => {
    const AuthenticatedRoute = (props) => {
      const user = useSelector((state) => state.auth.user);
      const authLoaded = useSelector((state) => state.auth.loaded);
      const authLoading = useSelector((state) => state.auth.loading);
      const isAsyncDataLoaded = useSelector(
        (state) => state.reduxAsyncConnect.loaded
      );

      // Wait for auth to finish loading before redirecting — avoids treating an
      // in-flight /me as logged-out (which caused /messages ↔ /signin loops).
      if (!authLoaded || authLoading || !isAsyncDataLoaded) {
        return <Loader loading />;
      }

      if (user) {
        return <Component {...props} />;
      }

      if (__CLIENT__) {
        // Not authenticated, handle redirect
        const currentPath = props.location.pathname;
        const isAlreadyRedirecting =
          window.location.pathname.includes('/signin') &&
          window.location.search.includes(`referer=${currentPath}`);

        // Sentry logging to track potential infinite redirects
        if (window.location.pathname.includes('/signin')) {
          const referrer = document.referrer;

          // Check if we might be in a redirect loop
          if (
            referrer &&
            (referrer.includes('/signin') || referrer === currentPath)
          ) {
            let shouldLog = true;
            let lastLogged = null;

            try {
              lastLogged = sessionStorage.getItem('twine_redirect_loop_logged');
            } catch (e) {
              // Session storage might be unavailable
            }

            if (lastLogged) {
              const now = new Date().getTime();
              const fiveMinutesAgo = now - 5 * 60 * 1000;

              if (parseInt(lastLogged, 10) > fiveMinutesAgo) {
                shouldLog = false;
              }
            }

            if (shouldLog) {
              Sentry.captureMessage(
                'Potential redirect loop in requireLoginRender',
                {
                  level: 'warning',
                  tags: { component: 'requireLoginRender' },
                  extra: {
                    currentPath,
                    referrer,
                    isAlreadyRedirecting,
                    userAgent: navigator.userAgent,
                    authState: getState().auth,
                    timestamp: new Date().toISOString(),
                  },
                }
              );

              // Update the last logged timestamp
              try {
                sessionStorage.setItem(
                  'twine_redirect_loop_logged',
                  new Date().getTime().toString()
                );
              } catch (e) {
                // Session storage might be unavailable
              }
            }
          }
        }

        if (!isAlreadyRedirecting) {
          window.location = url || `${signInUrl}/signin?referer=${currentPath}`;
        }
        return null;
      }
      return null;
    };

    // Add PropTypes validation
    AuthenticatedRoute.propTypes = {
      location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
        search: PropTypes.string,
        hash: PropTypes.string,
        state: PropTypes.object,
      }).isRequired,
    };

    return AuthenticatedRoute;
  };

  // Guard components for /signin and /signup to redirect already-authenticated users
  const AuthenticatedSignInGuard = createAuthGuard(SignIn);
  const AuthenticatedSignUpGuard = createAuthGuard(SignUp);

  const noAuthRoutes = [
    {
      path: '/signin',
      component: AuthenticatedSignInGuard,
    },
    {
      path: '/signup',
      component: AuthenticatedSignUpGuard,
    },
  ];

  const authOnlyRoutes = [
    {
      path: '/apply-for-verification',
      exact: true,
      component: ApplyForVerification,
    },
    {
      path: '/ari',
      exact: true,
      component: Ari,
    },
    {
      path: '/ari/job/:externalJobId',
      exact: true,
      component: AriJobLanding,
    },
    {
      path: '/onboarding/microprojects',
      component: MicroProjects,
    },
    {
      path: '/onboarding/notifications',
      component: OnboardingNotifications,
    },
    {
      path: '/onboarding/client',
      component: OnboardingClient,
    },
    {
      path: '/onboarding',
      component: Onboarding,
    },
    {
      path: '/notifications',
      component: Notifications,
    },

    {
      // 'manage-briefs'
      path: '/manage/briefs/:briefId?/(pay)?/:pitchId?',
      component: ManageBriefsWrapper,
    },
    {
      // 'manage-pitches'
      path: '/manage/pitches/:pitchId?/(new-bill)?',
      component: ManagePitchesWrapper,
    },
    {
      // 'messages',
      path: '/messages/:username?',
      component: Messages,
    },
    {
      path: '/projects/:id/questions/:questionId',
      component: AnswerPublicQuestion,
    },
    {
      path: '/projects/create',
      component: CreateProject,
    },
    {
      path: '/:username/c:hash/:title?/text-only-edit',
      component: EditProject,
    },
    {
      path: '/projects/manage',
      component: ManageProjects,
    },
    {
      path: '/report/messages/:messageId',
      component: ReportMessages,
    },
    {
      path: '/saved-jobs',
      component: SavedJobs,
    },
    {
      path: '/shortlist',
      component: Shortlist,
    },
    {
      path: '/settings/:section?',
      component: Settings,
    },
    {
      path: '/payments',
      component: ViewPayments,
    },
    {
      path: '/earnings',
      component: ViewEarnings,
    },
    {
      path: '/users_integrations_oauth/undock',
      component: Undock,
    },
    {
      path: '/ats/recent',
      exact: true,
      component: RecentJobRedirect,
    },
    {
      path: '/ats',
      exact: true,
      component: Dashboard,
    },
  ].map(({ component, ...rest }) => ({
    ...rest,
    component: requireLoginRender(component),
  }));

  const universalRoutes = [
    {
      path: '/',
      exact: true,
      // component: Home,
      component: isLoggedIn()
        ? () => (
            <Redirect
              to={
                user && user.type === 'buyer'
                  ? getBuyerLoginPath()
                  : getSellerLoginPath()
              }
            />
          )
        : Home,
    },
    {
      path: '/redirecting',
      exact: true,
      component: isLoggedIn()
        ? ({ location }) => {
            // Check for originalReferer parameter from AuthCode component
            const params = new URLSearchParams(location.search);
            const originalReferer = params.get('originalReferer');

            if (originalReferer) {
              // Redirect to the original referer path
              const redirectPath = originalReferer.startsWith('/')
                ? originalReferer
                : `/${originalReferer}`;
              return <Redirect to={redirectPath} />;
            }

            // Default redirect based on user type
            return (
              <Redirect
                to={
                  user && user.type === 'buyer'
                    ? getBuyerLoginPath()
                    : getSellerLoginPath()
                }
              />
            );
          }
        : Redirecting,
    },
    {
      path: '/collaborate',
      exact: true,
      component: ({ location }) => (
        <Redirect
          to={{
            pathname: '/jobs',
            search: location.search,
            state: { statusCode: 301 },
          }}
        />
      ),
    },
    {
      path: [
        '/jobs/:role?/(in)/:country/:city?',
        '/collaborate/:role?',
        '/jobs/:role?',
        '/jobs',
        '/jobs-test',
      ],
      exact: true,
      component: Projects,
    },
    {
      path: '/ats/:id',
      exact: true,
      component: JobDetails,
    },
    {
      path: '/projects/:id?/:subsection?/:pitchId?',
      component: BriefDetailsWrapper,
    },
    {
      path: '/activate',
      component: Activate,
    },
    {
      path: '/auth/code',
      component: AuthenticatedAuthCodeGuard,
    },
    {
      path: '/browse',
      component: Browse,
    },
    {
      path: '/browse-old',
      component: BrowseOld,
    },
    {
      path: '/search',
      component: ({ location }) => (
        <Redirect
          to={{
            pathname: '/browse',
            search: location.search,
          }}
        />
      ),
    },
    {
      path: '/tag',
      component: () => <Redirect to="/browse" />,
    },
    {
      path: '/tag/%23:query',
      component: ({ location }) => (
        <Redirect to={`/browse/%23${location.query}`} />
      ),
    },
    {
      path: '/tag/:query',
      component: ({ location }) => (
        <Redirect to={`/browse/%23${location.query}`} />
      ),
    },
    {
      path: '/testimonials/:id/:authcode',
      component: TestimonialsUpdate,
    },
    {
      path: '/aup',
      component: Aup,
    },
    {
      path: '/engagement-terms',
      component: EngagementTerms,
    },
    {
      path: '/platform-terms',
      component: PlatformTerms,
    },
    {
      path: '/copyright',
      component: Copyright,
    },
    {
      path: '/howitworks/:tab?',
      component: HowItWorks,
    },
    {
      path: '/tools/betterpic',
      component: BetterPic,
    },
    {
      path: '/whyusetwine',
      component: WhyUseTwine,
    },
    {
      path: '/enterprise',
      component: Enterprise,
    },
    {
      path: '/hire-a-freelancer/:category?',
      component: Categories,
    },
    // PostAJob multi-step UI flow (known step paths only)
    {
      path: Object.values(STEP_PATHS),
      component: PostAJobFlowShell,
      routes: [
        {
          path: '/post/intent',
          exact: true,
          component: PostAJobIntent,
        },
        {
          path: '/post/category',
          exact: true,
          component: PostAJobCategory,
        },
        {
          path: '/post/role',
          exact: true,
          component: PostAJobRole,
        },
        {
          path: '/post/skills',
          exact: true,
          component: PostAJobSkills,
        },
        {
          path: '/post/capacity',
          exact: true,
          component: PostAJobCapacity,
        },
        {
          path: '/post/type',
          exact: true,
          component: PostAJobType,
        },
        {
          path: '/post/duration',
          exact: true,
          component: PostAJobDuration,
        },
        {
          path: '/post/experience',
          exact: true,
          component: PostAJobExperience,
        },
        {
          path: '/post/likelihood',
          exact: true,
          component: PostAJobLikelihood,
        },
        {
          path: '/post/locations',
          exact: true,
          component: PostAJobLocations,
        },
        {
          path: '/post/title-and-description',
          exact: true,
          component: PostAJobTitleAndDescription,
        },
        {
          path: '/post/deadline',
          exact: true,
          component: PostAJobDeadline,
        },
        {
          path: '/post/examples',
          exact: true,
          component: PostAJobExamples,
        },
        {
          path: '/post/budget',
          exact: true,
          component: PostAJobBudget,
        },
        {
          path: '/post/about-you',
          exact: true,
          component: PostAJobAboutYou,
        },
        {
          path: '/post/email',
          exact: true,
          component: PostAJobEmail,
        },
        {
          path: '/post/unpaid-work',
          exact: true,
          component: PostAJobUnpaidWork,
        },
      ],
    },
    // Legacy: /post and /post/:role → Fillout post-a-job
    {
      path: '/post/:role?',
      component: ({ match }) => {
        if (__CLIENT__) {
          const role = match?.params?.role;
          window.location = role
            ? `/post-a-job?role=${toSingular(role).toLowerCase()}`
            : '/post-a-job';
        }
        return null;
      },
    },
    {
      path: '/post-manual/:role?',
      component: Post,
    },
    {
      path: ['/find/:role?/with/:skill?', '/find/:role?/:country?/:city?'],
      render: FindRedirectWrapper,
    },
    {
      path: '/get/:role/:country?/:city?',
      component: ({ location }) => {
        const redirect = getRedirRoute('get', location);

        return (
          __CLIENT__ &&
          (window.location = redirect
            ? `${signInUrl}/${redirect.pathname}`
            : `${signInUrl}`)
        );
      },
    },
    {
      path: ['/pricing', '/pricing/creative'],
      component: Pricing,
    },
    {
      path: '/instagram/:skill',
      render: ({ match }) => (
        <Redirect
          to={`/get/${
            match.params.skill
          }?utm_source=instagram&utm_campaign=twine${match.params.skill.replace(
            /-/g,
            ''
          )}`}
        />
      ),
    },

    // Example: https://www.twine.net/LeifAndrew/c1qk7t0/views/views
    {
      path: '/:username/:hash/:project/views',
      render: ({ match }) => (
        <Redirect
          to={`/${match.params.username}/${match.params.hash}/${match.params.project}`}
        />
      ),
    },
    {
      path: '/:username/c:hash/:title?/:credits?',
      exact: true,
      component: Project,
    },
    {
      path: '/:username/:option?',
      exact: true,
      component: Portfolio,
    },
    {
      path: '/*',
      component: NotFound,
    },
  ];

  return [
    {
      component: App,
      routes: [...authOnlyRoutes, ...noAuthRoutes, ...universalRoutes],
    },
  ];
};
